import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, X } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import type { LessonDTO } from '../../../core/types/lesson.types';
import { slideRegistry } from '../../../core/plugin-system/SlideRegistry';
import { cn } from '../../../../../shared/utils';

interface PreviewModeProps {
  lesson: LessonDTO;
  onExit: () => void;
}

export const PreviewMode: React.FC<PreviewModeProps> = ({
  lesson,
  onExit,
}) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const currentSlide = lesson.slides?.[currentSlideIndex];
  const plugin = currentSlide ? slideRegistry.get(currentSlide.type!) : null;

  const goToNext = () => {
    if (currentSlideIndex < (lesson.slides?.length || 0) - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    }
  };

  const goToPrevious = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  };

  const isFirst = currentSlideIndex === 0;
  const isLast = currentSlideIndex === (lesson.slides?.length || 0) - 1;

  return (
    <div className="fixed inset-0 bg-gray-900 z-50 flex flex-col">
      {/* Header */}
      <div className="bg-gray-800 text-white px-6 py-4 flex items-center justify-between border-b border-gray-700">
        <div className="flex-1">
          <h2 className="text-lg font-semibold">{lesson.title}</h2>
          <p className="text-sm text-gray-300">Mod previzualizare</p>
        </div>

        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-300">
            Slide {currentSlideIndex + 1} / {lesson.slides?.length || 0}
          </div>
          <Button
            onClick={onExit}
            variant="ghost"
            size="icon"
            className="text-white hover:bg-gray-700"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-gray-800 px-6 py-2">
        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-lime-accent transition-all duration-300"
            style={{
              width: `${((currentSlideIndex + 1) / (lesson.slides?.length || 1)) * 100}%`,
            }}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-muted p-6">
        <div className="max-w-4xl mx-auto">
          {currentSlide && plugin ? (
            <div className="bg-card rounded-lg shadow-lg p-8">
              {/* Slide Type Badge */}
              <div className="flex items-center gap-2 mb-6 pb-4 border-b">
                <plugin.icon className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">
                  {plugin.displayName}
                </span>
                <span className="ml-auto text-sm text-muted-foreground">
                  Slide {currentSlideIndex + 1}
                </span>
              </div>

              {/* Slide Content */}
              <plugin.PreviewComponent
                slide={currentSlide}
              />
            </div>
          ) : (
            <div className="bg-card rounded-lg shadow-lg p-8 text-center text-muted-foreground">
              Nu există conținut disponibil pentru acest slide
            </div>
          )}
        </div>
      </div>

      {/* Navigation Footer */}
      <div className="bg-gray-800 text-white px-6 py-4 flex items-center justify-between border-t border-gray-700">
        <Button
          onClick={goToPrevious}
          disabled={isFirst}
          variant="outline"
          className="gap-2 bg-transparent text-white border-gray-600 hover:bg-gray-700 hover:text-white"
        >
          <ChevronLeft className="w-4 h-4" />
          Anterior
        </Button>

        <div className="flex gap-2">
          {lesson.slides?.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlideIndex(index)}
              className={cn(
                'w-3 h-3 rounded-full transition-all',
                index === currentSlideIndex
                  ? 'bg-lime-accent w-8'
                  : 'bg-gray-400 hover:bg-gray-300'
              )}
              title={`Slide ${index + 1}`}
            />
          ))}
        </div>

        <Button
          onClick={goToNext}
          disabled={isLast}
          variant="outline"
          className="gap-2 bg-transparent text-white border-gray-600 hover:bg-gray-700 hover:text-white"
        >
          Următor
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export default PreviewMode;

